// libraries
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { FlatList, Text, View } from 'react-native';
import Checkbox from 'expo-checkbox';
import styled from 'styled-components/native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import * as Animatable from 'react-native-animatable';
import { CustomModal } from '@components/molecules';

// components
import {
  CustomText,
  DivRow,
  Heading,
  SmallButton,
  SpacerColumn,
  SpacerRow,
} from '@components/atoms';
import {
  ActivitesCompleteModal,
  ActivitiesModal,
  BikeAndKitModal,
  RiderWithdrawnModal,
  SORTING_TYPE,
} from '@components/organisms';
import { RiderTrainingItem } from './components/RiderTrainingItem';
import { RiderHeaderTools } from './components/RiderHeaderTools';
import { TrainingFooterButton } from './components/TrainingFooterButton';
import { TrainingOptionModal } from './components/TrainingOptionModal';

// misc
import { layout } from '@styles/layout';
import { DashboardStackParamList } from '@utils/navigation';
import { genericStyles } from '@styles/genericStyles';
import { DeliveryDatumRider } from '@screens/dashboard/types';
import { useGetDeliveryCourseAndRiders } from '@hooks/useGetDeliveryCourseAndRiders';
import { useSubmitSavedDelivery } from '@hooks/useSubmitSavedDelivery';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { selectGeneralInstructorNotes, selectInstructorNotesByRiders } from '@screens/dashboard/selectors';
import { flexSearch } from '@utils/text';

const DONT_SHOW_WITHDRAWN_WARNING_KEY = 'dontShowWithdrawnRiderWarningPopup';

export const TrainingScreen = () => {
  // variables
  const [trainingRider, setTrainingRider] = useState<DeliveryDatumRider>();
  const [isTrainingVisible, toggleTraining] = useReducer(
    value => !value,
    localStorage.getItem('showActivity') === 'true' ? true : false,
  );
  const [isTrainingOptionsVisible, toggleTrainingOptions] = useReducer(value => !value, false);
  const [isKitCheckVisible, toggleKitCheck] = useReducer(value => !value, false);
  const [kitCheckRider, setKitCheckRider] = useState<DeliveryDatumRider>();
  const [withdrawnRider, setWithdrawnRider] = useState<DeliveryDatumRider>();
  const [withdrawnRiderNoParentModalVisible, setWithdrawnRiderNoParentModalVisible] =
    useState<DeliveryDatumRider>();
  const [dontShowWithdrawnWarning, setDontShowWithdrawnWarning] = useState(false);

  if (localStorage.getItem('showActivity') === 'true') {
    localStorage.removeItem('showActivity');
    if (!isTrainingVisible) {
      toggleTraining();
    }
  }

  const [showActivitiesCompleteModal, toggleActivitesCompleteModal] = useReducer(
    value => !value,
    false,
  );
  const {
    params: { deliveryId, courseId },
  } = useRoute<RouteProp<DashboardStackParamList, 'training'>>();
  const { setOptions, replace, navigate } =
    useNavigation<NativeStackNavigationProp<DashboardStackParamList>>();
  const { riders, delivery, course, riderIds } = useGetDeliveryCourseAndRiders();
  const [searchText, setSearchText] = useState('');
  const [sorting, setSorting] = useState<SORTING_TYPE>(null);

  const searchedRiders = useMemo(() => {
    const filteredRiders = riders.filter(rider => flexSearch(rider.name, searchText));

    if (sorting === 'YEAR_GROUP') {
      filteredRiders.sort((a, b) => {
        if (a.yearGroup < b.yearGroup) {
          return -1;
        }
        if (a.yearGroup > b.yearGroup) {
          return 1;
        }
        return 0;
      });
    } else if (sorting === 'A-TO-Z') {
      filteredRiders.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    }

    return filteredRiders;
  }, [searchText, riders, sorting]);

  const instructorNoters = useAppSelector(state =>
    selectInstructorNotesByRiders(state, delivery.id, riderIds),
  );
  const instructorGeneralNotes = useAppSelector(state =>
    selectGeneralInstructorNotes(state, delivery?.id),
  );

  const instructorNotesBadgeCount = useMemo(() => {
    const riderNotesCount =
      instructorNoters != null
        ? Object.values(instructorNoters).reduce((prev, cur) => prev + cur.notes.length, 0)
        : 0;

    const generalNotesCount =
      instructorGeneralNotes != null && !Array.isArray(instructorGeneralNotes)
        ? Object.values(instructorGeneralNotes).reduce((prev, cur) => prev + cur.notes.length, 0)
        : 0;

    return riderNotesCount + generalNotesCount;
  }, [instructorNoters, instructorGeneralNotes]);

  const { submit, isLoading } = useSubmitSavedDelivery('Activites updated successfully!', {
    onSuccess: () => {
      toggleActivitesCompleteModal();
      isTrainingVisible && toggleTraining();
      setTrainingRider(null);
    },
  });

  // hooks
  useEffect(() => {
    if (course) {
      setOptions({ title: `${course.courseLabelShortName} ${delivery.shortDescription ? ` - ${delivery.shortDescription}` : ''} Training ` });
    }
  }, []);

  useEffect(() => {
    if (withdrawnRiderNoParentModalVisible) {
      setDontShowWithdrawnWarning(false);
    }
  }, [withdrawnRiderNoParentModalVisible]);

  // functions
  const handlePressRiderWithdrawn = (rider: DeliveryDatumRider) => {
    if (localStorage.getItem(DONT_SHOW_WITHDRAWN_WARNING_KEY) === 'true') {
      setWithdrawnRider(rider);
      return;
    }
    setWithdrawnRiderNoParentModalVisible(rider);
  };

  const handleDontShowWithdrawnWarningChange = (newValue: boolean) => {
    setDontShowWithdrawnWarning(newValue);
  };

  const handleConfirmWithdrawnWarning = () => {
    const rider = withdrawnRiderNoParentModalVisible;
    if (!rider) {
      return;
    }
    if (dontShowWithdrawnWarning) {
      localStorage.setItem(DONT_SHOW_WITHDRAWN_WARNING_KEY, 'true');
    } else {
      localStorage.removeItem(DONT_SHOW_WITHDRAWN_WARNING_KEY);
    }
    setWithdrawnRider(rider);
    setWithdrawnRiderNoParentModalVisible(undefined);
  };

  const onCompleteActivites = () => {
    submit();
  };

  const onCompleteTraining = () => {};

  // returns
  const renderHeader = useCallback(
    () => (
      <RiderHeaderTools
        RenderHeader={() => (
          <View style={genericStyles.rowWithCenterAndSB}>
            <CustomText color="primary" size={20} font="bodyBold">
              {`My Riders: ${searchedRiders.length} selected`}
            </CustomText>

            <DivRow>
              <SmallButton
                title="Change"
                onPress={() =>
                  replace('allRiders', {
                    deliveryId,
                    courseId,
                  })
                }
              />
              <SpacerRow size={0.5} />
              <SmallButton
                title="Clear"
                onPress={() =>
                  replace('allRiders', {
                    deliveryId,
                    courseId,
                    isClearRiders: true,
                  })
                }
              />
            </DivRow>
          </View>
        )}
        onChangeSearchText={setSearchText}
        onSortingChange={settings => setSorting(settings['category_id'])}
      />
    ),
    [],
  );

  return (
    <Container>
      <Heading title={delivery.hostName} status={delivery.status} showSchoolIcon />

      <Content>
        <FlatList
          data={searchedRiders}
          ListHeaderComponent={renderHeader}
          renderItem={({ item }) => (
            <RiderTrainingItem
              courseShortName={course.courseLabelShortName}
              onPressTraining={() => setTrainingRider(item)}
              onPressInstructorFeedback={() =>
                navigate('instructorFeedback', {
                  deliveryId,
                  courseId,
                  riderId: item.id,
                })
              }
              onPressBikeAndKitCheck={() => setKitCheckRider(item)}
              // onPressRiderWithdrawn={() => setWithdrawnRider(item)}
              onPressRiderWithdrawn={() => handlePressRiderWithdrawn(item)}
              onPressRidersDetails={() => navigate('riderDetail', { riderId: item.id, deliveryId })}
              {...item}
            />
          )}
          keyExtractor={item => item.id.toString()}
          ItemSeparatorComponent={() => <SpacerColumn size={1} />}
          contentContainerStyle={{
            paddingHorizontal: layout.padding_x1,
            paddingVertical: layout.padding_x2,
          }}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
        />
      </Content>

      <Footer>
        <Animatable.View animation="pulse" easing="ease-in" iterationCount={5}>
          <TrainingFooterButton
            type="notes"
            onPress={() => navigate('instructorNotes', { courseId, deliveryId })}
            badgeCount={instructorNotesBadgeCount}
          />
        </Animatable.View>
        <Animatable.View animation="pulse" easing="ease-in" iterationCount={5}>
          <TrainingFooterButton type="training" onPress={toggleTraining} />
        </Animatable.View>
        <Animatable.View animation="pulse" easing="ease-in" iterationCount={5}>
          <TrainingFooterButton
            type="survey"
            onPress={() => navigate('trainingSurvey', { courseId, deliveryId })}
          />
        </Animatable.View>
        <Animatable.View animation="pulse" easing="ease-in" iterationCount={5}>
          <TrainingFooterButton
            type="feedback"
            onPress={() => navigate('instructorFeedback', { deliveryId, courseId })}
          />
        </Animatable.View>
        <Animatable.View animation="pulse" easing="ease-in" iterationCount={5}>
          {/* <TrainingFooterButton type="check-complete" onPress={toggleTrainingOptions} /> */}
          <TrainingFooterButton
            type="check-complete"
            onPress={() => {
              navigate('home', {
                screen: 'trainingChecklist',
                params: { deliveryId: deliveryId, courseId: courseId },
              });
            }}
          />
        </Animatable.View>
      </Footer>

      <ActivitiesModal
        isVisible={isTrainingVisible || !!trainingRider}
        course={course}
        color={course.courseColour}
        selectedRider={trainingRider}
        onComplete={onCompleteActivites}
        onClose={trainingRider ? () => setTrainingRider(undefined) : toggleTraining}
        isLoading={isLoading}
      />

      <ActivitesCompleteModal
        isVisible={showActivitiesCompleteModal}
        color={course.courseColour}
        onClose={toggleActivitesCompleteModal}
        onRestartTraining={toggleTraining}
      />

      <BikeAndKitModal
        isVisible={!!kitCheckRider || isKitCheckVisible}
        onClose={() => {
          if (kitCheckRider) {
            setKitCheckRider(undefined);
          } else {
            toggleKitCheck();
          }
        }}
        riders={kitCheckRider ? [kitCheckRider] : riders}
        deliveryId={deliveryId}
      />

      <RiderWithdrawnModal
        isVisible={!!withdrawnRider}
        onClose={() => setWithdrawnRider(undefined)}
        rider={withdrawnRider}
      />

      <CustomModal isVisible={!!withdrawnRiderNoParentModalVisible} title="Rider Withdrawn" onClose={() => setWithdrawnRiderNoParentModalVisible(undefined)}>
        <Content>
          <SpacerColumn size={2} />

          <CustomText textAlign="center" font="bodyBold">
            {withdrawnRiderNoParentModalVisible?.name}
          </CustomText>
          <SpacerColumn size={2} />

          <CustomText textAlign="center" font="bodyBold" style={{ marginHorizontal: 10 }}>
            <b style={{ color: 'red' }}>Please only select Withdrawn if a rider started the course but cannot continue.</b>
          </CustomText>
          <SpacerColumn size={3} />
          
          <CustomText textAlign="center" style={{ marginHorizontal: 20 }}>
            You must still complete the data for the time the rider participated
          </CustomText>
          <SpacerColumn size={3} />

          <CustomText textAlign="center">
            (Use <b>'Not Seen'</b> or <b>'Absent'</b> where appropriate.)
          </CustomText>
          <SpacerColumn size={3} />

          <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 30 }}>
            <Checkbox
              disabled={false}
              value={dontShowWithdrawnWarning}
              onValueChange={handleDontShowWithdrawnWarningChange}
            />
            <Text style={{ marginLeft: 10 }}>Don't show this popup again.</Text>
          </View>
          <SpacerColumn size={3} />

          <SmallButton title="I confirm I have read and understood the above" onPress={handleConfirmWithdrawnWarning} />
          <SpacerColumn size={1} />
        </Content>
      </CustomModal>

      <TrainingOptionModal
        color={course.courseColour}
        isVisible={isTrainingOptionsVisible}
        onClose={toggleTrainingOptions}
        onStartTraining={() => {
          toggleTrainingOptions();
          toggleTraining();
        }}
        onTrainingChecklist={() => {
          navigate('home', {
            screen: 'trainingChecklist',
            params: { deliveryId: deliveryId, courseId: courseId },
          });
          toggleTrainingOptions();
        }}
        onCompleteTraining={onCompleteTraining}
      />
    </Container>
  );
};

const Container = styled.View(({ theme: { colors } }) => ({
  backgroundColor: colors.primaryBackground,
  flex: 1,
}));

const Content = styled.View(() => ({
  flex: 1,
}));

const Footer = styled.View({
  ...genericStyles.rowEvenly,
});
